/**
 * Data Model Helpers - Standardize property access across the application
 * This eliminates the need to check multiple property variations everywhere
 */

/**
 * Standardized property getters for association data
 */
export const getAssociationProperty = {
  /**
   * Get unit count - standardized to use 'doors' property
   * All records in database use this canonical property
   */
  unitCount: association => {
    return association?.doors || null;
  },

  /**
   * Get monthly revenue - standardized to use 'monthly_revenue' property
   * All records in database use this canonical property
   */
  monthlyRevenue: association => {
    return association?.monthly_revenue || null;
  },

  /**
   * Get contact name - standardized to use 'contactName' property
   * All records in database use this canonical property
   */
  contactName: association => {
    return association?.contactName || null;
  },

  /**
   * Get contact email - standardized to use 'contactEmail' property
   * All records in database use this canonical property
   */
  contactEmail: association => {
    return association?.contactEmail || null;
  },

  /**
   * Get contact phone - standardized to use 'contactPhone' property
   * All records in database use this canonical property
   */
  contactPhone: association => {
    return association?.contactPhone || null;
  },

  /**
   * Get association name - standardized to use 'name' property
   * This is the actively edited field in the current system
   */
  name: association => {
    return association?.name || null;
  },
};

/**
 * Check if association has missing critical data
 */
export const hasMissingData = association => {
  const unitCount = getAssociationProperty.unitCount(association);
  const monthlyRevenue = getAssociationProperty.monthlyRevenue(association);

  return !unitCount || !monthlyRevenue;
};

/**
 * Get all missing data fields for an association
 */
export const getMissingDataFields = association => {
  const missing = [];

  if (!getAssociationProperty.unitCount(association)) {
    missing.push('Unit Count');
  }

  if (!getAssociationProperty.monthlyRevenue(association)) {
    missing.push('Monthly Revenue');
  }

  if (!getAssociationProperty.contactName(association)) {
    missing.push('Contact Name');
  }

  if (!getAssociationProperty.contactEmail(association)) {
    missing.push('Contact Email');
  }

  return missing;
};

/**
 * Normalize association data to standardized property names
 * This can be used to convert incoming data to a consistent format
 */
export const normalizeAssociation = association => {
  return {
    ...association,
    // Standardized properties
    _unitCount: getAssociationProperty.unitCount(association),
    _monthlyRevenue: getAssociationProperty.monthlyRevenue(association),
    _contactName: getAssociationProperty.contactName(association),
    _contactEmail: getAssociationProperty.contactEmail(association),
    _contactPhone: getAssociationProperty.contactPhone(association),
    _name: getAssociationProperty.name(association),
    _hasMissingData: hasMissingData(association),
    _missingFields: getMissingDataFields(association),
  };
};

/**
 * Property mapping for database updates
 * Maps standardized property names to the actual database field names
 */
export const getDatabasePropertyName = {
  unitCount: 'doors', // Use 'doors' as the canonical database field
  monthlyRevenue: 'monthly_revenue', // Use 'monthly_revenue' as canonical
  contactName: 'contactName',
  contactEmail: 'contactEmail',
  contactPhone: 'contactPhone',
};

/**
 * Safely prepare updates for Cosmos DB with patch operation limit
 * Cosmos DB has a limit of 10 patch operations per request
 */
export const prepareSafeUpdate = (currentData, updates, maxOperations = 10) => {
  // Remove undefined, null, and unchanged values
  const filteredUpdates = {};
  let operationCount = 0;

  for (const [key, value] of Object.entries(updates)) {
    // Skip if value is undefined or null
    if (value === undefined || value === null) continue;

    // Skip if value hasn't changed, BUT special-case name fields where legacy and new may differ
    if (currentData && currentData[key] === value) {
      // If updating 'name' and providing 'associationName' (or vice versa), allow both to pass through
      if (
        !(
          ['name', 'associationName'].includes(key) &&
          (updates.name !== currentData.name ||
            updates.associationName !== currentData.associationName)
        )
      ) {
        continue;
      }
    }

    // Skip if we're at the operation limit
    if (operationCount >= maxOperations) {
      console.warn(
        `Skipping field '${key}' - reached Cosmos DB patch operation limit (${maxOperations})`
      );
      continue;
    }

    filteredUpdates[key] = value;
    operationCount++;
  }

  console.log(`Prepared ${operationCount} update operations:`, filteredUpdates);
  return filteredUpdates;
};

/**
 * Get only the essential fields for updates to avoid patch operation limits
 * Updated to use only canonical property names after data model standardization
 */
export const getEssentialFields = updates => {
  const essentialFields = [
    'doors', // Canonical unit count field
    'monthly_revenue', // Canonical revenue field
    'status',
    'followUp',
    'stalled',
    'lastActivity', // Status fields
    'contactName',
    'contactEmail',
    'contactPhone', // Contact fields
    'activity',
    'notes',
    'proposals', // Important data fields
  ];

  const essentialUpdates = {};
  let count = 0;

  for (const field of essentialFields) {
    if (updates[field] !== undefined && count < 10) {
      essentialUpdates[field] = updates[field];
      count++;
    }
  }

  return essentialUpdates;
};

export default {
  getAssociationProperty,
  hasMissingData,
  getMissingDataFields,
  normalizeAssociation,
  getDatabasePropertyName,
  prepareSafeUpdate,
  getEssentialFields,
};
